/**
 * Desktop WebView origin is `http://tauri.localhost`, so browser fetch to
 * `https://api.toko.uz` is blocked by CORS. Route allowlisted HTTPS API calls
 * through a Tauri command instead.
 */

type ProxyResult = {
  status: number;
  headers: Array<[string, string]>;
  body: string;
};

declare global {
  interface Window {
    __TOKO_TAURI_API_FETCH__?: boolean;
  }
}

function isTauriShell(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function allowedHosts(): Set<string> {
  const hosts = new Set(["api.toko.uz", "localhost", "127.0.0.1"]);
  for (const raw of [
    import.meta.env.VITE_GRAPHQL_URL,
    import.meta.env.VITE_BACKEND_URL,
    import.meta.env.VITE_STORAGE_URL,
    import.meta.env.VITE_TOKO_OAUTH_TOKEN_URL,
    import.meta.env.VITE_TOKO_OAUTH_AUTHORIZE_URL,
  ]) {
    const value = raw?.trim();
    if (!value) continue;
    try {
      hosts.add(new URL(value).hostname.toLowerCase());
    } catch {
      /* ignore */
    }
  }
  return hosts;
}

function shouldProxy(url: string): boolean {
  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.href : undefined);
    const host = parsed.hostname.toLowerCase();
    if (parsed.protocol !== "https:" && host !== "localhost" && host !== "127.0.0.1") {
      return false;
    }
    if (allowedHosts().has(host)) return true;
    return host.endsWith(".toko.uz") && parsed.protocol === "https:";
  } catch {
    return false;
  }
}

async function collectHeaders(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Array<[string, string]>> {
  const out: Array<[string, string]> = [];
  const push = (key: string, value: string) => {
    out.push([key, value]);
  };
  if (input instanceof Request) {
    input.headers.forEach((value, key) => push(key, value));
  }
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((value, key) => push(key, value));
    } else if (Array.isArray(init.headers)) {
      for (const [key, value] of init.headers) push(key, value);
    } else {
      for (const [key, value] of Object.entries(init.headers)) {
        if (value != null) push(key, String(value));
      }
    }
  }
  return out;
}

async function collectBody(
  input: RequestInfo | URL,
  init?: RequestInit,
  method?: string,
): Promise<string | null> {
  const m = (method ?? "GET").toUpperCase();
  if (m === "GET" || m === "HEAD") return null;
  if (init?.body != null) {
    if (typeof init.body === "string") return init.body;
    if (init.body instanceof URLSearchParams) return init.body.toString();
    if (typeof Blob !== "undefined" && init.body instanceof Blob) return init.body.text();
    if (init.body instanceof ArrayBuffer) return new TextDecoder().decode(init.body);
    if (ArrayBuffer.isView(init.body)) {
      const view = init.body;
      return new TextDecoder().decode(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength));
    }
    return String(init.body);
  }
  if (input instanceof Request) {
    try {
      return await input.clone().text();
    } catch {
      return null;
    }
  }
  return null;
}

/** Install once at app boot (before GraphQL / OAuth network calls). */
export function installTauriApiFetchProxy(): void {
  if (!isTauriShell() || window.__TOKO_TAURI_API_FETCH__) return;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = requestUrl(input);
    if (!shouldProxy(url)) {
      return nativeFetch(input, init);
    }

    const method = (
      init?.method ||
      (input instanceof Request ? input.method : "GET") ||
      "GET"
    ).toUpperCase();
    const headers = await collectHeaders(input, init);
    const body = await collectBody(input, init, method);

    const { invoke } = await import("@tauri-apps/api/core");
    const result = await invoke<ProxyResult>("http_proxy_request", {
      url,
      method,
      headers,
      body,
    });

    return new Response(result.body, {
      status: result.status,
      statusText: String(result.status),
      headers: Object.fromEntries(result.headers),
    });
  };

  window.__TOKO_TAURI_API_FETCH__ = true;
}
